import { useQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import * as styles from './ExternalPostsView.module.scss';
import PostFilterMenu from './PostFilterMenu';
import PostRow from './PostRow';
import PostSectionHeader from './PostSectionHeader';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import EmptyState from '../components/EmptyState';
import type { BrandAndProductProps } from '../components/ExternalBrandLogo';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import { gql } from '../graphql/generated/gql';
import {
  ExternalPostsFilter,
  ExternalPostsOrder,
} from '../graphql/generated/graphql';
import allPostsIcon from '../icons/non-prefilled/16x16/allPosts.svg';
import archiveIcon from '../icons/non-prefilled/16x16/archive.svg';
import orderIcon from '../icons/non-prefilled/16x16/order.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import templateIcon from '../icons/non-prefilled/16x16/template.svg';
import verifiedIcon from '../icons/non-prefilled/16x16/verified.svg';
import { UIActions } from '../ui/uiSlice';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import PageMeta from '../utils/PageMeta';
import { externalDisplayName } from '../utils/post/externalPost';

const METADATA_ATTRIBUTE = {
  [ExternalPostsOrder.Popularity]: 'popularity',
  [ExternalPostsOrder.RecentlyCreated]: 'publishedAt',
  [ExternalPostsOrder.RecentlyUpdated]: 'updatedAt',
  [ExternalPostsOrder.Alphabetical]: 'updatedAt',
  [ExternalPostsOrder.RecentlyViewed]: 'userViewedAt',
} as const;

const EXTERNAL_POSTS_QUERY = gql(/** @complexity 17 */ `
  query externalPosts(
    $id: String!
    $orderBy: ExternalPostsOrder
    $filter: ExternalPostsFilter
    $afterCursor: String
  ) {
    platform: externalPostPlatform(id: $id) {
      id
      allPostsCount: postsCount(filter: ALL)
      verifiedPostsCount: postsCount(filter: VERIFIED)
      publishedPostsCount: postsCount(filter: PUBLISHED)
      archivedPostsCount: postsCount(filter: ARCHIVED)
      templatePostsCount: postsCount(filter: TEMPLATE)
      posts(
        after: $afterCursor
        first: 25
        orderBy: $orderBy
        filter: $filter
      ) {
        edges {
          node {
            views
            userViewedAt
            updatedAt
            ...PostRow_post
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`);

const ExternalPostsView = () => {
  const dispatch = useAppDispatch();
  const { platform: platformId } = useParams() as { platform: string };
  const [type, setType] = useState(ExternalPostsFilter.Published);
  const orderBy =
    useAppSelector((state) => state.ui.externalPostsOrder) ||
    ExternalPostsOrder.Popularity;

  const response = useQuery(EXTERNAL_POSTS_QUERY, {
    variables: { id: platformId, orderBy, filter: type },
  });

  const fetchMore = ConnectionHelper.generateFetchMore(
    EXTERNAL_POSTS_QUERY,
    response,
    'platform.posts',
  );

  const platform = response.data?.platform;

  if (!platform) {
    return null;
  }

  const { id } = platform;
  const displayName = externalDisplayName(id);
  const docTitle = displayName ? `${displayName} - Slab` : 'Slab';

  const posts = _.compact(platform.posts?.edges?.map((edge) => edge?.node));

  const counts: Record<ExternalPostsFilter, number> = {
    [ExternalPostsFilter.All]: platform.allPostsCount,
    [ExternalPostsFilter.Archived]: platform.archivedPostsCount,
    [ExternalPostsFilter.Verified]: platform.verifiedPostsCount,
    [ExternalPostsFilter.Published]: platform.publishedPostsCount,
    [ExternalPostsFilter.Template]: platform.templatePostsCount,
  };

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <PageMeta title={docTitle} />
      <div className={styles.container}>
        <PageContent>
          <div>
            <PageTitle
              title={
                <h1 className={styles.mainTitle}>
                  <div className={styles.titleLogo}>
                    <ExternalBrandLogo
                      {...({ brand: id } as BrandAndProductProps)}
                      height={32}
                    />
                  </div>
                  {displayName}
                </h1>
              }
              subtitle={`These posts are synced from Markdown files in your ${displayName} repositories.`}
            />
            <PostSectionHeader
              controls={
                <>
                  <PostFilterMenu
                    onChange={setType}
                    options={{
                      [ExternalPostsFilter.Published]: {
                        icon: postIcon,
                        count: counts.PUBLISHED,
                        label: 'Published',
                      },
                      [ExternalPostsFilter.Verified]: {
                        icon: verifiedIcon,
                        count: counts.VERIFIED,
                        label: 'Verified',
                      },
                      [ExternalPostsFilter.Template]: {
                        icon: templateIcon,
                        count: counts.TEMPLATE,
                        label: 'Template',
                      },
                      [ExternalPostsFilter.Archived]: {
                        icon: archiveIcon,
                        count: counts.ARCHIVED,
                        label: 'Archived',
                      },
                      [ExternalPostsFilter.All]: {
                        icon: allPostsIcon,
                        count: counts.ALL,
                        label: 'All posts',
                      },
                    }}
                    title="Show"
                    value={type}
                  />
                  <PostFilterMenu
                    icon={orderIcon}
                    onChange={(value) =>
                      dispatch(UIActions.setExternalPostsOrder(value))
                    }
                    options={{
                      [ExternalPostsOrder.Popularity]: { label: 'Popularity' },
                      [ExternalPostsOrder.RecentlyCreated]: {
                        label: 'Recently created',
                      },
                      [ExternalPostsOrder.RecentlyUpdated]: {
                        label: 'Recently updated',
                      },
                      [ExternalPostsOrder.RecentlyViewed]: {
                        label: 'Recently viewed',
                      },
                      [ExternalPostsOrder.Alphabetical]: {
                        label: 'Alphabetical',
                      },
                    }}
                    menuPlacement="bottom-end"
                    title="Sort"
                    value={orderBy}
                  />
                </>
              }
              fullPageWidth={false}
            >
              <h3>Synced posts</h3>
            </PostSectionHeader>
            {posts.length ? (
              <>
                {posts.map((post) => (
                  <PostRow
                    key={post.id}
                    post={post}
                    meta={
                      <PostRow.Meta
                        attribute={METADATA_ATTRIBUTE[orderBy]}
                        values={{
                          userViewedAt: post.userViewedAt,
                          updatedAt: post.updatedAt,
                          popularity: post.views,
                          publishedAt: post.publishedAt,
                        }}
                      />
                    }
                  />
                ))}
              </>
            ) : (
              <EmptyState
                imageName="notFound"
                title="No posts found"
                message={`We couldn't find viewable posts synced from ${displayName}.`}
              />
            )}
          </div>

          <InfiniteScrollWaypoint fetchMore={fetchMore} />
        </PageContent>
      </div>
    </PageLayout.Content>
  );
};

export default ExternalPostsView;
